import React from "react";
import { Input } from "react-ui";
import { ColumnStack, Fit, RowStack } from "@skbkontur/react-stack-layout";

import { emptyRef } from "../../TypingUtils";
import { isEnterKey } from "../../../Helpers/KeyboardUtils";

import { TaxRebateLawPattern } from "./TaxRebate";
import { fakeFocus, LawContainer, LawNote } from "./TaxRebate.styles";
import { splitTaxRebateValue } from "./TaxRebateValueSplit";
import { buildValue, lawValueRegexp } from "./TaxRebateUtils";
import { TaxRebateLaw, TaxRebateLawProps } from "./TaxRebateLaw";

export interface TaxRebateLawsProps {
    value: string | undefined;
    lawPattern: TaxRebateLawPattern;
    focused: boolean;
    onChange: (nextValue: string) => void;
    law1Input: React.RefObject<Input>;
    parentInputCursorPosition: number;
    onLawEnterKeyDown?: () => void;
}

export function TaxRebateLaws(props: TaxRebateLawsProps): JSX.Element {
    const { lawPattern, focused, law1Input, parentInputCursorPosition, onLawEnterKeyDown } = props;
    const law2Input = React.useRef<Input>(emptyRef);
    const law3Input = React.useRef<Input>(emptyRef);

    const { law1Length, law2Length, law3Length, law1Name, law2Name, law3Name, lawNote } = lawPattern;
    const lawCharWidth = 16;
    const defaultLawWidth = 64;
    const law1Width = (law1Length ?? defaultLawWidth) * lawCharWidth;
    const law2Width = (law2Length ?? defaultLawWidth) * lawCharWidth;
    const law3Width = (law3Length ?? defaultLawWidth) * lawCharWidth;
    const containerWidth = law1Width + law2Width + law3Width + 24;

    const splitValue = splitTaxRebateValue(props.value ?? "", lawPattern);

    const handleChangeLaw1 = (nextValue: string) => {
        if (!lawValueRegexp.test(nextValue)) {
            law1Input.current?.blink();
            return;
        }
        if (nextValue.length === law1Length) {
            law2Input.current?.focus();
            law2Input.current?.setSelectionRange(0, 0);
        }
        if (nextValue.length > law1Length) {
            nextValue = nextValue.slice(0, law1Length);
        }
        props.onChange(
            buildValue(
                splitValue.code.value ?? "",
                { law1: nextValue, law2: splitValue.law2?.value, law3: splitValue.law3?.value },
                props.lawPattern
            )
        );
    };

    const handleLaw1KeyDown = (e: React.KeyboardEvent) => {
        if (isEnterKey(e)) {
            onLawEnterKeyDown && onLawEnterKeyDown();
        }
    };

    const handleChangeLaw2 = (nextValue: string) => {
        if (!lawValueRegexp.test(nextValue)) {
            law2Input.current?.blink();
            return;
        }
        if (nextValue.length === law2Length) {
            law3Input.current?.focus();
            law3Input.current?.setSelectionRange(0, 0);
        }
        if (nextValue.length > law2Length) {
            nextValue = nextValue.slice(0, law2Length);
        }
        props.onChange(
            buildValue(
                splitValue.code.value ?? "",
                { law1: splitValue.law1?.value, law2: nextValue, law3: splitValue.law3?.value },
                props.lawPattern
            )
        );
    };

    const handleLaw2KeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Backspace") {
            if ((splitValue.law2?.value === "" || splitValue.law2?.value == undefined) && law1Length) {
                law1Input.current?.focus();
                law1Input.current?.setSelectionRange(law1Length, law1Length);
                e.preventDefault();
                e.stopPropagation();
            }
        }
        if (isEnterKey(e)) {
            onLawEnterKeyDown && onLawEnterKeyDown();
        }
    };

    const handleChangeLaw3 = (nextValue: string) => {
        if (!lawValueRegexp.test(nextValue)) {
            law3Input.current?.blink();
            return;
        }
        if (nextValue.length >= law3Length) {
            nextValue = nextValue.slice(0, law3Length);
        }
        props.onChange(
            buildValue(
                splitValue.code.value ?? "",
                { law1: splitValue.law1?.value, law2: splitValue.law2?.value, law3: nextValue },
                props.lawPattern
            )
        );
    };

    const handleLaw3KeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Backspace") {
            if ((splitValue.law3?.value === "" || splitValue.law3?.value == undefined) && law2Length) {
                law2Input.current?.focus();
                law2Input.current?.setSelectionRange(law2Length, law2Length);
                e.preventDefault();
                e.stopPropagation();
            }
        }
        if (isEnterKey(e)) {
            onLawEnterKeyDown && onLawEnterKeyDown();
        }
    };

    const law1Props: TaxRebateLawProps = {
        lawName: law1Name,
        lawWidth: law1Width,
        lawInput: law1Input,
        lawInputProps: {
            ...{
                css:
                    focused &&
                    splitValue.law1 != undefined &&
                    splitValue.law1.start <= parentInputCursorPosition &&
                    parentInputCursorPosition <= splitValue.law1.end
                        ? fakeFocus
                        : undefined,
            },
            value: splitValue.law1?.value ?? "",
            width: law1Width,
            onValueChange: handleChangeLaw1,
            onKeyDown: handleLaw1KeyDown,
        },
    };

    const law2Props: TaxRebateLawProps = {
        lawName: law2Name,
        lawWidth: law2Width,
        lawInput: law2Input,
        lawInputProps: {
            ...{
                css:
                    focused &&
                    splitValue.law2 != undefined &&
                    splitValue.law2.start <= parentInputCursorPosition &&
                    parentInputCursorPosition <= splitValue.law2.end
                        ? fakeFocus
                        : undefined,
            },
            value: splitValue.law2?.value ?? "",
            width: law2Width,
            onValueChange: handleChangeLaw2,
            onKeyDown: handleLaw2KeyDown,
        },
    };

    const law3Props: TaxRebateLawProps = {
        lawName: law3Name,
        lawWidth: law3Width,
        lawInput: law3Input,
        lawInputProps: {
            ...{
                css:
                    focused &&
                    splitValue.law3 != undefined &&
                    splitValue.law3.start <= parentInputCursorPosition &&
                    parentInputCursorPosition <= splitValue.law3.end
                        ? fakeFocus
                        : undefined,
            },
            value: splitValue.law3?.value ?? "",
            width: law3Width,
            onValueChange: handleChangeLaw3,
            onKeyDown: handleLaw3KeyDown,
        },
    };

    return (
        <LawContainer style={{ width: containerWidth }}>
            <ColumnStack gap={2}>
                <Fit>
                    <RowStack gap={2}>
                        {law1Length ? <TaxRebateLaw {...law1Props} /> : null}
                        {law2Length ? <TaxRebateLaw {...law2Props} /> : null}
                        {law3Length ? <TaxRebateLaw {...law3Props} /> : null}
                    </RowStack>
                </Fit>
                <LawNote>{lawNote}</LawNote>
            </ColumnStack>
        </LawContainer>
    );
}
